import React, { useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Card,
  CardContent,
  Grid,
  InputAdornment,
  Chip,
  Tooltip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Menu,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Apartment as ApartmentIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  AttachMoney as MoneyIcon,
  LocationOn as LocationIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  FileDownload as FileDownloadIcon,
  FileUpload as FileUploadIcon,
} from '@mui/icons-material';
import {
  downloadExcelFile,
  exportAsCSV,
  exportAsExcel,
  exportAsPDF,
  readExcelFile,
} from '../../../utils/importExport';
import { hasReadPermission, hasWritePermission } from '../../../utils/menuFilter';
import WarningIcon from '@mui/icons-material/Warning';
import AvatarMui from '@mui/material/Avatar';

const ApartmentList = () => {
  const history = useHistory();
  const location = useLocation();
  const projectName = location.state?.projectName;

  const canRead = hasReadPermission('project.apartment-commercial') || hasReadPermission('project');
  const canWrite = hasWritePermission('project.apartment-commercial') || hasWritePermission('project');

  // State management
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchCity, setSearchCity] = useState('');
  const [searchCompany, setSearchCompany] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedApartment, setSelectedApartment] = useState(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importError, setImportError] = useState('');
  const [importSuccess, setImportSuccess] = useState('');
  const [importFileName, setImportFileName] = useState('');
  const [exportMenuAnchorEl, setExportMenuAnchorEl] = useState(null);
  const fileInputRef = useRef(null);

  // Mock data for apartments
  const [apartments, setApartments] = useState([
    {
      id: 1,
      apartmentId: 'APT001',
      badge: '2 BHK',
      badgeColor: '#4CAF50',
      name: 'Green Valley Apartments',
      developer: 'Yes ',
      status: 'Available',
      commission: '5%',
      type: 'Residential',
      description: 'Luxury apartments with modern amenities',
      amenities: 'Swimming Pool, Gym, Kids Play Area, 24/7 Security, Power Backup, Clubhouse, Garden',
      guidelineValue: '₹45,00,000',
      totalUnits: '120',
      reraNo: 'RERA-APT-2023-001',
      startDate: 'Mon, 10 Jan 2023 | 10-01-2023',
      completeDate: 'Fri, 31 Dec 2025 | 31-12-2025',
      phone: '+91 9876543210',
      email: 'amit@greenvalley.com',
      city: 'Bangalore',
      state: 'Karnataka',
      address: '456 Whitefield Main Road, Bangalore',
      bankName: 'HDFC Bank',
      accountNumber: '987654321012',
      ifscCode: 'HDFC0001234',
      possessionDate: 'Dec 2025',
      configuration: '2 BHK',
      carpetArea: '1200 sq.ft',
      floor: '5th to 15th',
      parking: '1 Covered + 1 Open',
    },
    {
      id: 2,
      apartmentId: 'APT002',
      badge: '3 BHK',
      badgeColor: '#2196F3',
      name: 'Skyline Residency',
      developer: 'Yes ',
      status: 'Available',
      commission: '6%',
      type: 'Luxury',
      description: 'Premium luxury apartments',
      amenities: 'Swimming Pool, Spa, Gym, Indoor Games, Jogging Track, Party Hall',
      guidelineValue: '₹75,00,000',
      totalUnits: '80',
      reraNo: 'RERA-APT-2023-002',
      startDate: 'Thu, 15 Mar 2023 | 15-03-2023',
      completeDate: 'Mon, 30 Jun 2026 | 30-06-2026',
      phone: '+91 9876543211',
      email: 'priya@skyline.com',
      city: 'Hyderabad',
      state: 'Telangana',
      address: '789 Gachibowli, Hyderabad',
      bankName: 'SBI',
      accountNumber: '987654321013',
      ifscCode: 'SBIN0001234',
      possessionDate: 'Jun 2026',
      configuration: '3 BHK',
      carpetArea: '1800 sq.ft',
      floor: '10th to 20th',
      parking: '2 Covered',
    },
  ]);

  // Helper functions
  const getFilteredApartments = () => {
    return apartments.filter((apartment) => {
      const matchesSearch = searchQuery ?
        (apartment.apartmentId.toLowerCase().includes(searchQuery.toLowerCase()) ||
          apartment.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          apartment.developer.toLowerCase().includes(searchQuery.toLowerCase())) : true;
      const matchesCity = searchCity ? apartment.city === searchCity : true;
      const matchesCompany = searchCompany ? apartment.developer === searchCompany : true;
      const matchesStatus = statusFilter === 'all' || apartment.status === statusFilter;
      const matchesType = typeFilter === 'all' || apartment.configuration === typeFilter ||
        (typeFilter === 'Luxury' && apartment.type === 'Luxury');
      return matchesSearch && matchesCity && matchesCompany && matchesStatus && matchesType;
    });
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'Available':
        return { backgroundColor: '#e8f5e9', color: '#2e7d32' };
      case 'Under Construction':
        return { backgroundColor: '#fff3e0', color: '#f57c00' };
      case 'Hold':
        return { backgroundColor: '#e3f2fd', color: '#1976d2' };
      case 'Booked':
        return { backgroundColor: '#f3e5f5', color: '#7b1fa2' };
      case 'Mortgage':
        return { backgroundColor: '#fff9c4', color: '#f57f17' };
      case 'Registry':
        return { backgroundColor: '#c8e6c9', color: '#388e3c' };
      default:
        return { backgroundColor: '#f5f5f5', color: '#666' };
    }
  };

  const handleView = (apartment) => {
    setSelectedApartment(apartment);
    setViewDialogOpen(true);
  };

  const handleEdit = (apartment) => {
    history.push({
      pathname: '/app/project/apartment/edit',
      state: { apartment: apartment }
    });
  };

  const handleCloseView = () => {
    setViewDialogOpen(false);
    setSelectedApartment(null);
  };

  const handleAddNew = () => {
    history.push('/app/project/apartment/add');
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this apartment?')) {
      setApartments(apartments.filter((item) => item.id !== id));
      setErrorMessage('');
      setSuccessMessage('Apartment deleted successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    }
  };


  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Search and filter handlers
  const handleSearch = () => {
    setPage(0);
    setErrorMessage('');
    setSuccessMessage(`Search completed. Found ${getFilteredApartments().length} apartments.`);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleReset = () => {
    setSearchQuery('');
    setSearchCity('');
    setSearchCompany('');
    setStatusFilter('all');
    setTypeFilter('all');
    setPage(0);
    setSuccessMessage('');
    setErrorMessage('');
  };

  const handleImportClick = () => {
    setImportError('');
    setImportSuccess('');
    setImportFileName('');
    setImportDialogOpen(true);
  };

  const handleCloseImportDialog = () => {
    setImportDialogOpen(false);
    setImportError('');
    setImportSuccess('');
    setImportFileName('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleOpenExportMenu = (event) => {
    setExportMenuAnchorEl(event.currentTarget);
  };

  const handleCloseExportMenu = () => {
    setExportMenuAnchorEl(null);
  };

  const handleImportFile = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;

    try {
      setImportFileName(file.name);
      const rows = await readExcelFile(file);
      if (!Array.isArray(rows) || rows.length === 0) {
        throw new Error('Excel sheet is empty or invalid.');
      }

      const normalized = rows.map((row, index) => ({
        id: row.id || row.ID || `imported-${Date.now()}-${index}`,
        apartmentId: row.apartmentId || row.ApartmentId || row['Apartment ID'] || `APT-${index + 1}`,
        badge: row.badge || row.Badge || row.configuration || row.Configuration || '',
        badgeColor: row.badgeColor || row.BadgeColor || '#4CAF50',
        name: row.name || row.Name || 'Unnamed Apartment',
        developer: row.developer || row.Developer || 'Yes ',
        status: row.status || row.Status || 'Available',
        commission: row.commission || row.Commission || '',
        type: row.type || row.Type || row.configuration || row.Configuration || '',
        description: row.description || row.Description || '',
        amenities: row.amenities || row.Amenities || '',
        guidelineValue: row.guidelineValue || row.GuidelineValue || row['Circle Rate'] || '',
        totalUnits: row.totalUnits || row.TotalUnits || '',
        reraNo: row.reraNo || row.ReraNo || row['RERA No'] || '',
        startDate: row.startDate || row.StartDate || '',
        completeDate: row.completeDate || row.CompleteDate || '',
        phone: row.phone || row.Phone || '',
        email: row.email || row.Email || '',
        city: row.city || row.City || '',
        state: row.state || row.State || '',
        address: row.address || row.Address || '',
        bankName: row.bankName || row.BankName || '',
        accountNumber: row.accountNumber || row.AccountNumber || '',
        ifscCode: row.ifscCode || row.IFSC || row.IfscCode || '',
        possessionDate: row.possessionDate || row.PossessionDate || '',
        configuration: row.configuration || row.Configuration || '',
        carpetArea: row.carpetArea || row.CarpetArea || row['Carpet Area'] || '',
        floor: row.floor || row.Floor || '',
        parking: row.parking || row.Parking || '',
      }));

      setApartments(normalized);
      setImportSuccess(`Imported ${normalized.length} apartment(s) successfully.`);
    } catch (err) {
      setImportError(err.message || 'Failed to read the selected file.');
      setImportSuccess('');
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const buildExportRows = () => {
    return (filteredData.length ? filteredData : apartments).map((apartment) => ({
      ApartmentId: apartment.apartmentId,
      Name: apartment.name,
      Developer: apartment.developer,
      Configuration: apartment.configuration || apartment.badge,
      City: apartment.city,
      CarpetArea: apartment.carpetArea,
      Commission: apartment.commission,
      GuidelineValue: apartment.guidelineValue,
      Status: apartment.status,
      Amenities: apartment.amenities,
      Phone: apartment.phone,
      Email: apartment.email,
    }));
  };

  const showTransientSuccess = (message) => {
    setErrorMessage('');
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const showTransientError = (message) => {
    setSuccessMessage('');
    setErrorMessage(message);
    setTimeout(() => setErrorMessage(''), 4000);
  };

  const handleExportExcel = () => {
    try {
      const rows = buildExportRows();
      if (!rows.length) {
        showTransientError('No apartment data available to export.');
        return;
      }
      exportAsExcel({ rows, sheetName: 'Apartments', fileNameBase: 'apartments' });
      showTransientSuccess('Apartments exported to Excel successfully!');
    } catch (err) {
      showTransientError('Failed to export apartments to Excel.');
    } finally {
      handleCloseExportMenu();
    }
  };

  const handleExportCSV = () => {
    try {
      const rows = buildExportRows();
      if (!rows.length) {
        showTransientError('No apartment data available to export.');
        return;
      }
      exportAsCSV({ rows, fileNameBase: 'apartments' });
      showTransientSuccess('Apartments exported to CSV successfully!');
    } catch (err) {
      showTransientError('Failed to export apartments to CSV.');
    } finally {
      handleCloseExportMenu();
    }
  };

  const handleExportPDF = () => {
    try {
      const rows = buildExportRows();
      if (!rows.length) {
        showTransientError('No apartment data available to export.');
        return;
      }
      const headers = Object.keys(rows[0] || {});
      exportAsPDF({
        rows,
        headers,
        title: 'Apartment List',
        fileNameBase: 'apartments',
        headStyles: { fillColor: [25, 118, 210] },
      });
      showTransientSuccess('Apartments exported to PDF successfully!');
    } catch (err) {
      showTransientError('Failed to export apartments to PDF.');
    } finally {
      handleCloseExportMenu();
    }
  };

  const filteredData = getFilteredApartments();

  if (!canRead) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh', p: 3 }}>
        <Paper elevation={3} sx={{ p: { xs: 3, md: 6 }, textAlign: 'center', maxWidth: 500, borderRadius: 3, boxShadow: '0 10px 40px rgba(0,0,0,0.1)' }}>
          <AvatarMui sx={{ bgcolor: '#fee2e2', color: '#ef4444', width: 80, height: 80, mb: 3, mx: 'auto' }}>
            <WarningIcon sx={{ fontSize: 50 }} />
          </AvatarMui>
          <Typography variant="h4" fontWeight="bold" color="#1a202c" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, fontSize: '1.1rem' }}>
            You do not have permission to view Apartment Inventory. Please contact your administrator for access.
          </Typography>
          <Button
            variant="contained"
            onClick={() => history.push('/app/dashboard')}
            sx={{
              backgroundColor: '#536DFE',
              '&:hover': { backgroundColor: '#3d5afe' },
              px: 4, py: 1.2, borderRadius: 2, fontWeight: 600, textTransform: 'none'
            }}
          >
            Back to Dashboard
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, mr: 5 }}>
      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ApartmentIcon sx={{ mr: 1, color: 'primary.main', fontSize: 28 }} />
            <Typography variant="h4" fontWeight="bold" color="primary">
              Apartment List
            </Typography>
          </Box>
          {projectName && (
            <Typography variant="body2" color="text.secondary" sx={{ ml: 4.5 }}>
              Project: {projectName}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          {canWrite && (
            <>
              <Button
                variant="contained"
                startIcon={<FileUploadIcon />}
                onClick={handleImportClick}
                sx={{ px: 2.5, py: 1.3 }}
              >
                Import
              </Button>
              <Button
                variant="outlined"
                startIcon={<FileDownloadIcon />}
                onClick={handleOpenExportMenu}
                sx={{ px: 2.5, py: 1.3 }}
              >
                Export
              </Button>
              <Menu
                anchorEl={exportMenuAnchorEl}
                open={Boolean(exportMenuAnchorEl)}
                onClose={handleCloseExportMenu}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem onClick={handleExportExcel}>Export as Excel</MenuItem>
                <MenuItem onClick={handleExportCSV}>Export as CSV</MenuItem>
                <MenuItem onClick={handleExportPDF}>Export as PDF</MenuItem>
              </Menu>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddNew}
                sx={{
                  backgroundColor: 'primary.main',
                  '&:hover': { backgroundColor: '#1565c0' },
                  px: 3,
                  py: 1.5,
                }}
              >
                Add New Apartment
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
          {successMessage}
        </Alert>
      )}
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setErrorMessage('')}>
          {errorMessage}
        </Alert>
      )}

      {/* Search and Filter Section */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              label="Search Apartment ID/Name..."
              variant="outlined"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>City</InputLabel>
              <Select
                value={searchCity}
                onChange={(e) => setSearchCity(e.target.value)}
                label="City"
              >
                <MenuItem value="">All Cities</MenuItem>
                {[...new Set(apartments.map((a) => a.city))].map((city) => (
                  <MenuItem key={city} value={city}>
                    {city}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Company</InputLabel>
              <Select
                value={searchCompany}
                onChange={(e) => setSearchCompany(e.target.value)}
                label="Company"
              >
                <MenuItem value="">All Companies</MenuItem>
                {[...new Set(apartments.map((a) => a.developer))].map((company) => (
                  <MenuItem key={company} value={company}>
                    {company}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Configuration</InputLabel>
              <Select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                label="Configuration"
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="2 BHK">2 BHK</MenuItem>
                <MenuItem value="3 BHK">3 BHK</MenuItem>
                <MenuItem value="Luxury">Luxury</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="Under Construction">Under Construction</MenuItem>
                <MenuItem value="Available">Available</MenuItem>
                <MenuItem value="Hold">Hold</MenuItem>
                <MenuItem value="Booked">Booked</MenuItem>
                <MenuItem value="Mortgage">Mortgage</MenuItem>
                <MenuItem value="Registry">Registry</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleSearch}
                // startIcon={<SearchIcon />}
                sx={{ flex: 1 }}
              >
                Search
              </Button>
              <Button
                variant="outlined"
                onClick={handleReset}
                // startIcon={<RefreshIcon />}
                sx={{ flex: 1 }}
              >
                Reset
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Table Section */}
      <Paper sx={{ borderRadius: 2, boxShadow: 3, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Apartment ID</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Developer</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Configuration</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>City</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Carpet Area</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Commission</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}> Circle Rate</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.95rem' }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.length > 0 ? (
                filteredData
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((apartment) => (
                    <TableRow key={apartment.id} hover>
                      <TableCell>
                        <Typography fontWeight="medium" color="primary">
                          {apartment.apartmentId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="500">
                          {apartment.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {apartment.developer}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={apartment.configuration}
                          size="small"
                          sx={{
                            backgroundColor: apartment.badgeColor,
                            color: '#fff',
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <LocationIcon sx={{ mr: 0.5, fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2">
                            {apartment.city}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{apartment.carpetArea}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium" color="success.main">
                          {apartment.commission}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {apartment.guidelineValue}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={apartment.status}
                          size="small"
                          style={getStatusStyle(apartment.status)}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <Tooltip title="View Apartment">
                            <IconButton
                              color="info"
                              size="small"
                              onClick={() => handleView(apartment)}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                          {canWrite ? (
                            <>
                              <Tooltip title="Edit Apartment">
                                <IconButton
                                  color="primary"
                                  size="small"
                                  onClick={() => handleEdit(apartment)}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete Apartment">
                                <IconButton
                                  color="error"
                                  size="small"
                                  onClick={() => handleDelete(apartment.id)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </>
                          ) : (
                            <></>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
              ) : (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="textSecondary">
                      No apartments found matching your criteria.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination

          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onClose={handleCloseView} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h6" fontWeight="bold">
            Apartment Details - {selectedApartment?.apartmentId}
          </Typography>
          <IconButton onClick={handleCloseView} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          {selectedApartment && (
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Apartment ID</Typography>
                <Typography variant="body1" fontWeight="500">{selectedApartment.apartmentId}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Name</Typography>
                <Typography variant="body1" fontWeight="500">{selectedApartment.name}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Developer</Typography>
                <Typography variant="body1" fontWeight="500">{selectedApartment.developer}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Type</Typography>
                <Typography variant="body1" fontWeight="500">{selectedApartment.type}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Configuration</Typography>
                <Chip label={selectedApartment.configuration} size="small" sx={{ mt: 0.5, backgroundColor: selectedApartment.badgeColor, color: '#fff' }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Status</Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip label={selectedApartment.status} size="small" style={getStatusStyle(selectedApartment.status)} />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Carpet Area</Typography>
                <Typography variant="body1" fontWeight="500">{selectedApartment.carpetArea}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Floor Range</Typography>
                <Typography variant="body1" fontWeight="500">{selectedApartment.floor}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Parking</Typography>
                <Typography variant="body1" fontWeight="500">{selectedApartment.parking}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Commission</Typography>
                <Typography variant="body1" fontWeight="500" color="success.main">{selectedApartment.commission}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Circle Rate</Typography>
                <Typography variant="body1" fontWeight="500">{selectedApartment.guidelineValue}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">RERA No</Typography>
                <Typography variant="body1" fontWeight="500">{selectedApartment.reraNo}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Possession Date</Typography>
                <Typography variant="body1" fontWeight="500">{selectedApartment.possessionDate}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Address</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Full Address</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                  <LocationIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body1" fontWeight="500">{selectedApartment.address}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">City</Typography>
                <Typography variant="body1" fontWeight="500">{selectedApartment.city}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">State</Typography>
                <Typography variant="body1" fontWeight="500">{selectedApartment.state}</Typography>
              </Grid>

            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseView} variant="outlined">Close</Button>
        </DialogActions>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onClose={handleCloseImportDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Import Apartments</DialogTitle>
        <DialogContent dividers>
          {importError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setImportError('')}>
              {importError}
            </Alert>
          )}
          {importSuccess && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setImportSuccess('')}>
              {importSuccess}
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              Upload an Excel file (.xlsx or .xls) with apartment columns such as{' '}
              <code>apartmentId</code>, <code>name</code>, <code>developer</code>, <code>configuration</code>, <code>city</code>,
              <code>carpetArea</code>, <code>commission</code>, <code>guidelineValue</code>, and <code>status</code>. Missing columns will use default values.
            </Typography>
            <Button
              variant="text"
              startIcon={<FileDownloadIcon />}
              onClick={() => {
                try {
                  downloadExcelFile({
                    rows: [
                      {
                        apartmentId: 'APT001',
                        name: 'Sample Apartment',
                        developer: 'Sample Developer',
                        configuration: '3 BHK',
                        city: 'Bangalore',
                        carpetArea: '1450 sq.ft',
                        commission: '5%',
                        guidelineValue: '₹65,00,000',
                        status: 'Available',
                        amenities: 'Swimming Pool, Gym',
                      },
                    ],
                    sheetName: 'Sample',
                    fileName: 'apartment_import_sample.xlsx',
                  });
                } catch (err) {
                  setImportError(err.message || 'Failed to download sample file.');
                }
              }}
            >
              Download Sample Format
            </Button>
            <Button
              variant="text"
              startIcon={<FileDownloadIcon />}
              onClick={() => {
                try {
                  downloadExcelFile({
                    rows: [
                      {
                        apartmentId: '',
                        name: '',
                        developer: '',
                        configuration: '',
                        city: '',
                        carpetArea: '',
                        commission: '',
                        guidelineValue: '',
                        status: '',
                        amenities: '',
                      },
                    ],
                    sheetName: 'Template',
                    fileName: 'apartment_import_template.xlsx',
                  });
                } catch (err) {
                  setImportError(err.message || 'Failed to download template file.');
                }
              }}
            >
              Download Simple File
            </Button>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button variant="outlined" onClick={() => fileInputRef.current?.click()}>
                Choose File
              </Button>
              {importFileName && (
                <Typography variant="caption" color="text.secondary">
                  {importFileName}
                </Typography>
              )}
            </Box>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              style={{ display: 'none' }}
              onChange={handleImportFile}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseImportDialog} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default ApartmentList;